<template>
  <div class="order-report-download">
    <v-data-table
      v-model:page="page"
      :headers="headers"
      :items="reports"
      :items-per-page="itemsPerPage"
      :loading="isLoading"
      :loading-text="t('Loading Content')"
      :no-data-text="t('No data available')"
      hover
      dense
      hide-default-footer
    >

      <template #item.fileName="{ item }">
        <FileTooltip :file="item">
          <template v-slot:content>
            <v-list-item-title v-text="item.fileName"></v-list-item-title>
          </template>
        </FileTooltip>
      </template>

      <template #item.comment="{ item }">
        <div class="comment">{{ item.comment }}</div>
        <v-tooltip
          location="bottom"
          color="primary"
          attach="#approot"
        >
          <template #activator="{ props : activatorProps }">
            <div
              v-bind="activatorProps"
            >
              <slot name="content" />
            </div>
          </template>
          <div class="tooltip-content">
            <span v-if="item.comment">{{ $t("Comment") }}: </span>
            {{ item.comment }}
          </div>
        </v-tooltip>
      </template>

      <template #item.updatedAt="{ item }" class="text-no-wrap">
        {{ normalizeDate(item.updatedAt, true) ?? '-' }}
      </template>

      <template #item.actions="{ item }">
        <div class="download-button">
          <Button
            :icon-btn="true"
            color="black"
            size="default"
            variant="plain"
            :disabled="isLoading"
            @click="downloadReport(item)"
          >
            <v-progress-circular
              v-if="loadingOrderFileId === item.orderFileId && loadedValue !== null"
              indeterminate
              color="primary"
              size="20"
              width="2"
            />
            <v-icon v-else>mdi-download-outline</v-icon>
          </Button>
        </div>
      </template>

    </v-data-table>

    <v-pagination
      v-if="reports.length > itemsPerPage"
      v-model="page"
      :length="Math.ceil(reports.length / itemsPerPage)"
      class="mt-4"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, watch} from 'vue';
import { useReportsStore } from "@/stores/reports"
import { useSupportingFilesStore } from "@/stores/supportingFiles"
import { Report } from "@/entities/report"
import { useI18n } from "vue-i18n"
import Button from "@/components/input-components/Button.vue"
import {formatDate, normalizeDate} from "@/composables/useDateHelper";
import FileTooltip from "@/components/common/FileTooltip.vue";

defineOptions({ name: "OrderReportDownload" })

const { t } = useI18n()

const props = defineProps<{
  reports: Report[]
  loadedValue?: number | null
  reportType: 'reports' | 'supportingFiles'
}>()

const emit = defineEmits<{
  (e: 'isLoading', value: boolean): void
}>()

const page = ref(1);
const loadingOrderFileId = ref<number | null>(null)
const intervalRef = ref<ReturnType<typeof setInterval> | null>(null)

const isLoading = computed(() => loadingOrderFileId.value !== null)
const itemsPerPage = ref(10);

const headers = computed(() => [
  { title: t("File name"), value: "fileName", sortable: false, width: "38%" },
  { title: t("Comment"), value: "comment", sortable: false, width: "35%" },
  { title: t("Updated at"), value: "updatedAt", sortable: false, width: "15%", headerProps: { class: "text-no-wrap" } },
  { title: "", value: "actions", sortable: false, width: "12%" },
])

watch(isLoading, (newValue) => {
  emit('isLoading', newValue);
}, { immediate: true });

function clearPolling() {
  if (intervalRef.value) {
    clearInterval(intervalRef.value)
    intervalRef.value = null
  }
}

const currentStore = computed(
  () => props.reportType === 'supportingFiles' ? useSupportingFilesStore() : useReportsStore()
)

onUnmounted(() => {
  clearPolling()
})

async function downloadReport(report: Report) {
  const store = currentStore.value

  loadingOrderFileId.value = report.orderFileId

  intervalRef.value = setInterval(() => {
    store.fetchLoadedValue()
  }, 500)

  try {
    await store.fetchOrderReport({
      orderId: report.orderId,
      orderFileId: report.orderFileId,
      fileName: report.fileName,
    })
  } catch (err) {
    console.warn("Error downloading report", err)
  } finally {
    clearPolling()
    store.fetchLoadedValue()
    loadingOrderFileId.value = null
  }
}
</script>

<style lang="scss">
.order-report-download {
  width: 100%;

  table {
    table-layout: fixed;
    width: 100%;

    td, .v-list-item-title {
      font-size: 0.777rem !important;
    }
  }
}

.download-row {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 0;
  box-sizing: border-box;
}

.file-name, .comment {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.download-button {
  flex-shrink: 0;
  margin-left: 12px;

  .v-btn__content {
      --v-btn-height: 24px !important;
  }
}
</style>
